import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';

export type ChecklistChartDescriptionSectionBadgeClass = 'yellow' | 'green' | 'red';

interface IChecklistChartDescriptionData {
    titleTextId: string;
    sections: IChecklistChartDescriptionSection[];
}

interface IChecklistChartDescriptionSection {
    badgeClassName: ChecklistChartDescriptionSectionBadgeClass;
    sectionTitleTextId: string;
    sectionDescriptionTextId: string;
}

@Component({
    selector: 'fi-checklist2-report-chart-description',
    template: `
        <mat-card class="fi-rounded-card" [ngClass]="cardClass">
          <mat-card-content>
            <mat-expansion-panel [hideToggle]="true" #expansionPanel>
              <mat-expansion-panel-header>
                <span class="card-header-text">{{
                  chartDescriptionsByReportType[reportType].titleTextId | fiAppText
                }}</span>
                <mat-icon class="far fa-chevron-down" />
              </mat-expansion-panel-header>
        
              @for (sectionData of chartDescriptionsByReportType[reportType].sections; track sectionData) {
                <div
                  class="chart-section-wrapper"
                  >
                  <div class="chart-section-name-wrapper">
                    <div class="indicator" [ngClass]="sectionData.badgeClassName"></div>
                    <span class="chart-section-name">{{ sectionData.sectionTitleTextId | fiAppText }}:</span>
                  </div>
                  <p class="chart-section-description">{{ sectionData.sectionDescriptionTextId | fiAppText }}</p>
                </div>
              }
            </mat-expansion-panel>
          </mat-card-content>
        </mat-card>
        `,
    styleUrl: './fi-checklist2-report-chart-description.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-report-chart-description' },
    standalone: false,
})
export class FiChecklist2ReportChartDescriptionComponent {
    @Input() reportType: FiChecklist2ReportType;
    @Input() cardClass: string = '';

    private readonly _openChecklistChartDescriptionData: IChecklistChartDescriptionData = {
        titleTextId: 'checklist.status_info',
        sections: [
            {
                badgeClassName: 'green',
                sectionTitleTextId: 'common.completed_plural',
                sectionDescriptionTextId: 'checklist.open_checklist_completed_description',
            },
            {
                badgeClassName: 'yellow',
                sectionTitleTextId: 'task.in_progress', // SWE plural
                sectionDescriptionTextId: 'checklist.open_checklist_in_progress_description',
            },
            {
                badgeClassName: 'red',
                sectionTitleTextId: 'checklist.not_started_yet',
                sectionDescriptionTextId: 'checklist.open_checklist_not_started_description',
            },
        ],
    };

    private readonly _checklistHistoryChartDescriptionData: IChecklistChartDescriptionData = {
        titleTextId: 'checklist.status_info',
        sections: [
            {
                badgeClassName: 'green',
                sectionTitleTextId: 'common.completed_plural',
                sectionDescriptionTextId: 'checklist.checklist_history_completed_description',
            },
            {
                badgeClassName: 'yellow',
                sectionTitleTextId: 'common.partially_completed',
                sectionDescriptionTextId: 'checklist.checklist_history_partially_completed_description',
            },
            {
                badgeClassName: 'red',
                sectionTitleTextId: 'common.missed',
                sectionDescriptionTextId: 'checklist.checklist_history_skipped_description',
            },
        ],
    };

    chartDescriptionsByReportType: { [key in FiChecklist2ReportType]?: IChecklistChartDescriptionData } = {
        [FiChecklist2ReportType.OpenChecklists]: this._openChecklistChartDescriptionData,
        [FiChecklist2ReportType.PastChecklists]: this._checklistHistoryChartDescriptionData,
    };
}
